import router from "@/router";
import axios from "axios";

export default ({
  daemonRequest = false,
  hasFile = false,
} = {}) => {
  const url = daemonRequest
    ? process.env.VUE_APP_SCREEN_PULSE_SOCKET_URL + "/api/"
    : process.env.VUE_APP_BASE_URL + "/api/";

  const options = {
    baseURL: url,
    withCredentials: true,
    headers: {
      Accept: "application/json",
      "Content-type": hasFile ? "multipart/form-data" : "application/json",
    },
  };

  const instance = axios.create(options);

  // Add request interceptor for debugging
  instance.interceptors.request.use(
    (config) => {
      // Log the request details for debugging
      // console.log('Making request to:', config.baseURL + config.url);
      // console.log('Request headers:', config.headers);
      return config;
    },
    (error) => Promise.reject(error),
  );

  instance.interceptors.response.use(
    (response) => {
      if (response.status === 200 || response.status === 201) {
        return Promise.resolve(response.data);
      } else {
        return Promise.reject(response);
      }
    },
    async (error) => {
      const originalRequest = error.config;

      // Retry once on 401. The access token may have just been rotated by a
      // concurrent request on the same page (Laravel revokes the old token the
      // instant it issues the new one, so an in-flight sibling request can carry a
      // token that was valid when it was sent but is revoked by the time it lands).
      // The retry re-authenticates via the httpOnly session cookie — Laravel injects
      // a fresh JWT — so this transient race no longer logs the user out. We only
      // fall through to logout if the retry itself still comes back 401.
      if (
        error?.response?.status === 401 &&
        originalRequest &&
        !originalRequest._retryAfter401
      ) {
        originalRequest._retryAfter401 = true;
        // Give any in-flight token refresh a moment to land in the session store.
        await new Promise((resolve) => setTimeout(resolve, 250));
        return instance(originalRequest);
      }

      if (error && error.response && error.response.status) {
        if (
          error.response &&
          error.response.data &&
          !error.response.data.success &&
          error.response.data.locked
        ) {
          router.push("/company-details");
        }
        switch (error.response.status) {
          case 400:
            //do something
            break;
          case 401:
            localStorage.clear();
            router.push("/login");
            break;
          case 403:
            // Intentionally does NOT redirect to /403. Route-level refusals are
            // handled by the router guard; a 403 from one background request
            // must not navigate the whole app away from a page that is
            // otherwise working. Callers surface their own error state.
            break;
          case 404:
            router.push("/404");
            break;
          case 502:
            console.error("502 Server Error:", error.response);
            break;
          case 500:
            console.error("500 Server Error:", error.response);
            break;
          case 307:
            router.push("/307", { query: { redirect: true } });
            break;
          default:
            console.error(`Error ${error.response.status}:`, error.response);
        }
        return Promise.reject(error.response);
      }
      return Promise.reject(error);
    },
  );

  return instance;
};
